import type { AdminUser } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  Switch,
  Textarea,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Mail } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { AdminPage } from '@/features/admin/admin-layout';
import { formatBytes, formatDate } from '@/features/shared/labels';
import { useFeature } from '@/features/shared/hooks';
import { api } from '@/lib/api';
import { useAuthMode } from '@/features/auth/identity';
import { useAuth } from '@/lib/auth/auth-provider';
import { describeError, isNotFound, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/feedback/toaster';

export function AdminUserDetailPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const user = useQuery({
    queryKey: queryKeys.admin.user(id),
    queryFn: () => api.admin.user(id),
    enabled: Boolean(id),
    retry: false,
  });

  return (
    <AdminPage section="users">
      <Link
        to="/admin/users"
        className="mb-4 inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"
      >
        <ArrowLeft aria-hidden className="size-4" />
        {t('admin.users.back')}
      </Link>
      {user.isLoading && <Spinner label={t('app.loading')} />}
      {user.error && (
        <Alert variant="destructive">
          {isNotFound(user.error) ? t('admin.users.notFound') : describeError(user.error)}
        </Alert>
      )}
      {user.data && <UserDetail user={user.data} />}
    </AdminPage>
  );
}

function UserDetail({ user }: { user: AdminUser }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const date = (value: string | null) => formatDate(value, i18n.language, true) || '–';

  const setAi = useMutation({
    mutationFn: (aiEnabled: boolean) => api.admin.updateUser(user.id, { aiEnabled }),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.admin.user(user.id), next);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success(
        t(next.aiEnabled ? 'admin.users.aiEnabledToast' : 'admin.users.aiDisabledToast'),
      );
    },
    onError: (e) => toastError(e),
  });
  const { user: me } = useAuth();
  const inbox = useFeature('support_inbox');
  const managedByIdentity = useAuthMode().mode === 'oidc';
  const [writeOpen, setWriteOpen] = useState(false);
  const setRole = useMutation({
    mutationFn: (role: AdminUser['role']) => api.admin.updateUser(user.id, { role }),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.admin.user(user.id), next);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success(t('admin.users.roleChanged', { role: t(`enums.userRole.${next.role}`) }));
    },
    onError: (e) => toastError(e),
  });

  return (
    <div className="space-y-6" data-testid="admin-user-detail">
      <header>
        <h1 className="text-2xl font-semibold">{user.displayName}</h1>
        <p className="text-sm text-muted-foreground">{user.email}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Badge variant={user.role === 'ADMIN' ? 'default' : 'outline'}>
            {t(`enums.userRole.${user.role}`)}
          </Badge>
          <Badge
            variant={
              user.status === 'ACTIVE'
                ? 'success'
                : user.status === 'DEACTIVATED'
                  ? 'destructive'
                  : 'warning'
            }
          >
            {t(`enums.userStatus.${user.status}`)}
          </Badge>
        </div>
      </header>

      <section aria-labelledby="user-role" className="rounded-lg border bg-card p-4">
        <h2 id="user-role" className="text-lg font-medium">
          {t('admin.users.roleTitle')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('admin.users.roleDescription')}</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Label htmlFor="user-role-select" className="sr-only">
            {t('admin.users.role')}
          </Label>
          <select
            id="user-role-select"
            className="h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-2"
            value={user.role}
            disabled={setRole.isPending || me?.id === user.id || managedByIdentity}
            onChange={(e) => setRole.mutate(e.target.value as AdminUser['role'])}
            data-testid="admin-user-role"
          >
            {(['USER', 'SUPPORT', 'ADMIN'] as const).map((role) => (
              <option key={role} value={role}>
                {t(`enums.userRole.${role}`)}
              </option>
            ))}
          </select>
          {me?.id === user.id && (
            <span className="text-sm text-muted-foreground">{t('admin.users.roleOwn')}</span>
          )}
          {managedByIdentity && (
            <span className="text-sm text-muted-foreground" data-testid="admin-user-identity">
              {t('admin.users.managedByIdentity')}
            </span>
          )}
          {inbox && (
            <Button
              variant="outline"
              className="ml-auto"
              onClick={() => setWriteOpen(true)}
              data-testid="admin-user-write"
            >
              <Mail aria-hidden /> {t('admin.users.write')}
            </Button>
          )}
        </div>
      </section>
      <WriteDialog user={user} open={writeOpen} onOpenChange={setWriteOpen} />

      <section aria-labelledby="user-ai" className="rounded-lg border bg-card p-4">
        <h2 id="user-ai" className="text-lg font-medium">
          {t('admin.users.aiTitle')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('admin.users.aiDescription')}</p>
        <div className="mt-4 flex items-center gap-3">
          <Switch
            id="user-ai-enabled"
            checked={user.aiEnabled}
            disabled={setAi.isPending || managedByIdentity}
            onCheckedChange={(checked) => setAi.mutate(checked)}
            data-testid="admin-user-ai"
          />
          <Label htmlFor="user-ai-enabled">{t('admin.users.aiSwitch')}</Label>
        </div>
        <p className="mt-3 text-sm text-muted-foreground" data-testid="admin-user-ai-consent">
          {user.aiConsentAt
            ? t(user.aiEnabled ? 'admin.users.aiConsentGiven' : 'admin.users.aiConsentKept', {
                date: date(user.aiConsentAt),
              })
            : t('admin.users.aiConsentMissing')}
        </p>
      </section>

      <section aria-labelledby="user-account" className="rounded-lg border bg-card p-4">
        <h2 id="user-account" className="text-lg font-medium">
          {t('admin.users.account')}
        </h2>
        <dl className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Fact label={t('admin.users.email')}>{user.email}</Fact>
          <Fact label={t('admin.users.emailVerified')}>
            {user.emailVerified ? t('common.yes') : t('common.no')}
          </Fact>
          <Fact label={t('admin.users.registered')}>{date(user.createdAt)}</Fact>
          <Fact label={t('admin.users.lastLogin')}>{date(user.lastLoginAt)}</Fact>
          <Fact label={t('admin.users.language')}>{user.locale.toUpperCase()}</Fact>
          <Fact label={t('admin.users.recipes')}>{user.recipeCount}</Fact>
          <Fact label={t('admin.users.storage')}>{formatBytes(user.storageBytes)}</Fact>
        </dl>
      </section>
    </div>
  );
}

/** "Write to the user": starts a support conversation from the support address. */
function WriteDialog({
  user,
  open,
  onOpenChange,
}: {
  user: AdminUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const send = useMutation({
    mutationFn: () =>
      api.admin.support.compose({ userId: user.id, subject: subject.trim(), text: text.trim() }),
    onSuccess: (conversation) => {
      toast.success(t('admin.support.sent'));
      navigate(`/admin/support/${conversation.id}`);
    },
    onError: (e) => toastError(e),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} data-testid="admin-user-write-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.users.writeTitle', { name: user.displayName })}</DialogTitle>
          <DialogDescription>
            {t('admin.users.writeDescription', { email: user.email })}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            send.mutate();
          }}
        >
          <div>
            <Label htmlFor="write-subject">{t('admin.mailTemplates.subject')}</Label>
            <Input
              id="write-subject"
              className="mt-1"
              required
              maxLength={200}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="admin-user-write-subject"
            />
          </div>
          <div>
            <Label htmlFor="write-text">{t('admin.users.writeMessage')}</Label>
            <Textarea
              id="write-text"
              className="mt-1"
              rows={6}
              required
              maxLength={20000}
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="admin-user-write-text"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={send.isPending}
              disabled={!subject.trim() || !text.trim()}
              data-testid="admin-user-write-send"
            >
              {t('admin.support.send')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}
